import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Box,
} from '@mui/material';
import type {
  VendorProduct,
  ProductMapping,
  CustomerVendorSubscription,
  ReconciliationVariance,
} from '@techgitter/contracts';
import { getProductMappings } from '../../services/productMappingService';
import { getVendorSubscriptions } from '../../services/vendorSubscriptionService';
import { fetchVariances } from '../../services/reconciliationApi';
import { updateVendorProduct } from '../../services/vendorProductService';
import { formatStatusLabel } from './reconciliationLabels';

const headCellSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  textTransform: 'uppercase' as const,
};
const bodyCellSx = { fontSize: '0.8rem' };

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

interface Props {
  open: boolean;
  vendorProduct: VendorProduct;
  /** Vendor display name, if known by the caller (the VP carries it too). */
  vendorName?: string | null;
  canEdit: boolean;
  onClose: () => void;
  /** Open the create-mapping dialog pre-filled with this vendor product. */
  onMap: (vp: VendorProduct) => void;
  /** Called after a successful activate/deactivate so the caller can refresh. */
  onChanged: () => void;
}

/**
 * What references a vendor product — the tool for deciding what to do with a
 * duplicate or unmapped one. Shows its identity (id / sku / term / active) plus
 * everything that points at this specific row: commercial mappings (active +
 * inactive), supplier-truth subscriptions, and open reconciliation variances.
 * The "live" one of a duplicate pair has subscriptions / variances bound to its
 * GUID; the orphan has none → deactivate it and map the live one.
 */
export const VendorProductDrillDownDialog: React.FC<Props> = ({
  open,
  vendorProduct,
  vendorName,
  canEdit,
  onClose,
  onMap,
  onChanged,
}) => {
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [subscriptions, setSubscriptions] = useState<CustomerVendorSubscription[]>([]);
  const [variances, setVariances] = useState<ReconciliationVariance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const vpId = vendorProduct.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const [maps, subs, vars] = await Promise.all([
          getProductMappings({ vendorProductId: vpId, includeInactive: true }, signal),
          getVendorSubscriptions({ vendorProductId: vpId }, signal),
          fetchVariances({ vendorProductId: vpId, status: 'open' }, signal),
        ]);
        if (signal?.aborted) return;
        setMappings(maps);
        setSubscriptions(subs);
        setVariances(vars);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load vendor product detail');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [vpId],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [open, load]);

  const referenced = subscriptions.length + variances.length;
  const activeMappingCount = useMemo(
    () => mappings.filter((m) => m.isActive).length,
    [mappings],
  );

  const setActive = async (isActive: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await updateVendorProduct(vpId, { isActive });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update vendor product');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <span>{vendorProduct.name || vendorProduct.sku}</span>
          <Chip size="small" label={vendorProduct.billingTerm} variant="outlined" />
          <Box component="span" sx={{ fontWeight: 600 }}>
            {formatStatusLabel(vendorProduct.isActive ? 'active' : 'inactive')}
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {vendorProduct.sku}
          {(vendorName ?? vendorProduct.vendorName) ? ` · ${vendorName ?? vendorProduct.vendorName}` : ''}
          {` · ${vpId}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Alert severity={referenced === 0 ? 'warning' : 'info'} variant="outlined">
              {referenced === 0 ? (
                <>
                  Nothing references this vendor product — no subscriptions and no open
                  variances. If it duplicates another, it's the safe one to{' '}
                  <strong>deactivate</strong>.
                </>
              ) : (
                <>
                  Referenced by {subscriptions.length} subscription
                  {subscriptions.length === 1 ? '' : 's'} and {variances.length} open variance
                  {variances.length === 1 ? '' : 's'} — this is a <strong>live</strong> record.
                  {activeMappingCount === 0 && ' It has no active mapping, so it has no billing coverage — map it.'}
                </>
              )}
            </Alert>

            <Section title={`Commercial mappings (${mappings.length})`}>
              {mappings.length === 0 ? (
                <Empty>No mapping — no billing coverage for this vendor product.</Empty>
              ) : (
                <SimpleTable
                  head={['Commercial product', 'Seat ×', 'Active']}
                  rows={mappings.map((m) => [
                    m.productName ?? m.productSku ?? '—',
                    String(m.seatMultiplier),
                    formatStatusLabel(m.isActive ? 'active' : 'inactive'),
                  ])}
                />
              )}
            </Section>

            <Divider />

            <Section title={`Subscriptions (${subscriptions.length})`}>
              {subscriptions.length === 0 ? (
                <Empty>No subscriptions point at this vendor product.</Empty>
              ) : (
                <SimpleTable
                  head={['Customer', 'Label', 'Seats', 'Status', 'Ref', 'Renewal']}
                  rows={subscriptions.map((s) => [
                    s.customerName ?? s.customerId ?? '—',
                    s.label ?? '—',
                    String(s.seats),
                    formatStatusLabel(s.status),
                    s.subscriptionRef ?? '—',
                    fmtDate(s.renewalDate),
                  ])}
                />
              )}
            </Section>

            <Divider />

            <Section title={`Open variances (${variances.length})`}>
              {variances.length === 0 ? (
                <Empty>No open variances reference this vendor product.</Empty>
              ) : (
                <SimpleTable
                  head={['Type', 'Customer', 'Expected', 'Vendor reported', 'Detected']}
                  rows={variances.map((v) => [
                    v.varianceType,
                    v.customerName ?? v.customerId ?? '—',
                    v.expectedValue ?? '—',
                    v.actualValue ?? '—',
                    fmtDate(v.detectedAt),
                  ])}
                />
              )}
            </Section>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        {canEdit && vendorProduct.isActive && (
          <Button
            color="warning"
            disabled={busy || loading}
            onClick={() => setActive(false)}
          >
            {busy ? 'Working…' : 'Deactivate'}
          </Button>
        )}
        {canEdit && !vendorProduct.isActive && (
          <Button color="success" disabled={busy || loading} onClick={() => setActive(true)}>
            {busy ? 'Working…' : 'Reactivate'}
          </Button>
        )}
        {canEdit && vendorProduct.isActive && (
          <Button variant="contained" disabled={busy} onClick={() => onMap(vendorProduct)}>
            Map
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Stack>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
);

const SimpleTable: React.FC<{ head: string[]; rows: string[][] }> = ({ head, rows }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          {head.map((h) => (
            <TableCell key={h} sx={headCellSx}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((cells, i) => (
          <TableRow key={i} sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}>
            {cells.map((c, j) => (
              <TableCell key={j} sx={bodyCellSx}>
                {c}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);
